<script lang="ts">
    import SvgIcons from "$lib/components/SvgIcons.svelte";
    import NotoEmoji from "$lib/components/NotoEmoji.svelte";
    import type { IMenuItem } from "./FlexibleMenu.types";
    import { logService } from "$lib/services/logService.svelte";
    import { customTooltip } from "$lib/actions/customTooltip";

    interface Props {
        item: IMenuItem;
        dataTestId: string;
    }

    let { item, dataTestId }: Props = $props();

    /**
     * Доступна назва кнопки — `label`, а якщо його немає, то `tooltip`; `id`
     * НІКОЛИ.
     *
     * Доти тут стояло `item.label || item.id`, і в кнопок без `label` назвою
     * ставав машинний ідентифікатор: читалка вимовляла «main-menu-link, кнопка»,
     * «test-mode-btn, кнопка», «dev-clear-cache-btn, кнопка». Заміряно в браузері
     * 2026-09-02 — п'ять кнопок із п'яти в лівому меню.
     *
     * Найгірше те, що людська назва в кожної з них БУЛА: у `tooltip` («Тестовий
     * режим», «Очистити кеш») — і йшла у візуальну підказку поруч. Тобто той,
     * хто дивиться, читав назву, а той, хто слухає, отримував ідентифікатор із
     * коду.
     *
     * Жоден гейт цього не бачив і не міг: axe перевіряє, що назва Є
     * (`button-name`), а `a11y-conventions.spec.ts` шукає ВІДСУТНІЙ `aria-label`.
     * Назва, яка є й нікуди не годиться, лежить рівно між ними.
     *
     * `id` лишається останнім запасом, щоб кнопка без обох полів не стала
     * безіменною зовсім — але тоді це видно в дереві доступності як явний
     * дефект, а не як мовчазна норма.
     */
    const accessibleName = $derived(item.label ?? item.tooltip ?? item.id);

    function handleClick(e: MouseEvent) {
        logService.action(`[MenuButton] Clicked: id=${item.id}, dataTestId=${dataTestId}`);
        if (item.onClick) {
            item.onClick(e);
        } else {
            logService.error(`[MenuButton] No onClick handler for item: ${item.id}`);
        }
    }
</script>

<button
    class="menu-button {item.isActive ? 'active' : ''} {item.primary
        ? 'primary'
        : ''}"
    onclick={handleClick}
    aria-label={accessibleName}
    type="button"
    data-testid={dataTestId}
    use:customTooltip={item.tooltip || item.label}
>
    <div class="icon-wrapper">
        {#if item.snippet}
            {@render item.snippet()}
        {:else if item.icon}
            <SvgIcons name={item.icon} />
        {:else if item.emoji}
            <!-- FIX: Прибрано хардкод перевірки на конкретні емодзі. 
                 Тепер компонент очікує валідну назву для NotoEmoji. -->
            <NotoEmoji name={item.emoji} size="1em" />
        {/if}
    </div>
</button>

<style>
    .menu-button {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        cursor: pointer;
        padding: 0.5rem;
        color: var(--text-secondary, #888); /* Fallback color */
        transition: all 0.2s ease;
        width: 100%;
        height: 100%;
        min-height: 48px; /* Minimum touch target */
        position: relative;
    }

    .menu-button:hover {
        color: var(--text-primary, #fff);
    }

    .menu-button.active {
        color: var(--text-accent, #ffaa00);
    }

    .icon-wrapper {
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 32px; /* Good default for emoji */
    }

    /* Primary (Central) Button Styling */
    .menu-button.primary {
        background: var(--control-bg, #444);
        color: var(--text-primary, #fff);
        border-radius: 50%;
        width: 64px; /* Fixed size for the FAB */
        height: 64px;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
    }

    /*
      Головна кнопка «Грати» брала зашитий `#4caf50` із білим текстом — 2.78:1,
      і жоден гейт цього не бачив: пара «літерал + літерал» під токенну
      перевірку не підпадає. Тепер вона йде за темою й лікується разом з усією
      палітрою.
    */
    [data-testid="menu-item-play"].primary {
        background: var(--confirm-action-bg, #4caf50);
        color: var(--confirm-action-text, #fff);
    }

    .menu-button.primary.active {
        background: var(--control-selected, #ffaa00);
        color: var(--control-selected-text, #000);
    }

    .menu-button.primary:hover {
        transform: scale(1.05);
        box-shadow: 0 6px 12px rgba(0, 0, 0, 0.4);
    }

    .menu-button.primary .icon-wrapper {
        width: 48px;
        height: 48px;
        font-size: 48px;
    }
</style>
